import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { OrderWithRelations } from "@/components/schedule/types";
import { useTenant } from "./useTenant";
import {
  ORDERS_WITH_LEGS_SELECT,
  ORDERS_WITH_LEGS_VIEW,
  mapLegRowToOrder,
  mapAndHydrateLegRows,
  hydrateLegRelations,
} from "@/lib/orders-with-legs";

// Phase 4 read-path migration: query the per-leg view so multi-leg orders surface
// each leg as its own assignment for the driver app (current/next/upcoming/recent).
const ORDER_SELECT = ORDERS_WITH_LEGS_SELECT;

export function useDriverAssignments(driverId: string | undefined) {
  const { tenantId } = useTenant();

  // Get current assignment (in_transit) - primary or secondary
  const { data: currentAssignment, isLoading: loadingCurrent } = useQuery({
    queryKey: ["driver-current-assignment", driverId, tenantId],
    queryFn: async () => {
      if (!driverId || !tenantId) return null;
      
      const { data, error } = await supabase
        .from(ORDERS_WITH_LEGS_VIEW)
        .select(ORDER_SELECT)
        .eq("tenant_id", tenantId)
        .or(`assigned_operator_id.eq.${driverId},assigned_operator_2_id.eq.${driverId}`)
        .in("status", ["booked", "confirmed", "in_transit"])
        .order("ready_time", { ascending: true })
        .limit(1)
        .maybeSingle();
      
      if (error) throw error;
      return data ? (await hydrateLegRelations([mapLegRowToOrder(data)]))[0] : null;
    },
    enabled: !!driverId && !!tenantId,
  });

  // Full list of "current assignments" (booked / confirmed / in_transit),
  // one row per leg. Drives the new driver landing list per the wireframe.
  const { data: currentAssignments, isLoading: loadingCurrentList } = useQuery({
    queryKey: ["driver-current-assignments", driverId, tenantId],
    queryFn: async () => {
      if (!driverId || !tenantId) return [];
      const { data, error } = await supabase
        .from(ORDERS_WITH_LEGS_VIEW)
        .select(ORDER_SELECT)
        .eq("tenant_id", tenantId)
        .or(`assigned_operator_id.eq.${driverId},assigned_operator_2_id.eq.${driverId}`)
        .in("status", ["booked", "confirmed", "in_transit"])
        .order("ready_time", { ascending: true });
      if (error) throw error;
      const hydrated = await mapAndHydrateLegRows(data);
      // Determine which parent orders are actually multi-leg by counting
      // total legs per parent (not just legs assigned to this driver).
      const parentIds = Array.from(
        new Set(hydrated.map((r: any) => r.parent_order_id).filter(Boolean)),
      ) as string[];
      const legCountByParent = new Map<string, number>();
      if (parentIds.length) {
        const { data: legRows } = await supabase
          .from("legs")
          .select("order_id")
          .in("order_id", parentIds);
        for (const lr of legRows ?? []) {
          const pid = (lr as any).order_id as string;
          legCountByParent.set(pid, (legCountByParent.get(pid) ?? 0) + 1);
        }
      }
      return hydrated.map((r: any) => ({
        ...r,
        _total_legs: legCountByParent.get(r.parent_order_id) ?? 1,
      }));
    },
    enabled: !!driverId && !!tenantId,
  });

  // Get next assignment (booked or confirmed, ready_time in the future or soonest)
  const { data: nextAssignment, isLoading: loadingNext } = useQuery({
    queryKey: ["driver-next-assignment", driverId, tenantId],
    queryFn: async () => {
      if (!driverId || !tenantId) return null;
      
      const { data, error } = await supabase
        .from(ORDERS_WITH_LEGS_VIEW)
        .select(ORDER_SELECT)
        .eq("tenant_id", tenantId)
        .or(`assigned_operator_id.eq.${driverId},assigned_operator_2_id.eq.${driverId}`)
        .in("status", ["booked", "confirmed"])
        .gte("ready_time", new Date().toISOString())
        .order("ready_time", { ascending: true })
        .limit(1)
        .maybeSingle();
      
      if (error) throw error;
      return data ? (await hydrateLegRelations([mapLegRowToOrder(data)]))[0] : null;
    },
    enabled: !!driverId && !!tenantId && !currentAssignment,
  });

  // Get upcoming assignments list
  const { data: upcomingAssignments, isLoading: loadingUpcoming } = useQuery({
    queryKey: ["driver-upcoming-assignments", driverId, tenantId],
    queryFn: async () => {
      if (!driverId || !tenantId) return [];
      
      const { data, error } = await supabase
        .from(ORDERS_WITH_LEGS_VIEW)
        .select(ORDER_SELECT)
        .eq("tenant_id", tenantId)
        .or(`assigned_operator_id.eq.${driverId},assigned_operator_2_id.eq.${driverId}`)
        .in("status", ["booked", "confirmed"])
        .gte("ready_time", new Date().toISOString())
        .order("ready_time", { ascending: true })
        .limit(5);
      
      if (error) throw error;
      return mapAndHydrateLegRows(data);
    },
    enabled: !!driverId && !!tenantId,
  });

  // Get recently completed trips (delivered in last 24h)
  const twentyFourHoursAgo = new Date(Date.now() - 24 * 60 * 60 * 1000).toISOString();
  const { data: recentlyCompleted, isLoading: loadingCompleted } = useQuery({
    queryKey: ["driver-recently-completed", driverId, tenantId],
    queryFn: async () => {
      if (!driverId || !tenantId) return [];
      
      const { data, error } = await supabase
        .from(ORDERS_WITH_LEGS_VIEW)
        .select(ORDER_SELECT)
        .eq("tenant_id", tenantId)
        .or(`assigned_operator_id.eq.${driverId},assigned_operator_2_id.eq.${driverId}`)
        .eq("status", "delivered")
        // The view exposes `leg_updated_at` / `order_updated_at`; legs reflect the most
        // recent change to either the leg row or the parent order, so use leg_updated_at.
        .gte("leg_updated_at", twentyFourHoursAgo)
        .order("leg_updated_at", { ascending: false })
        .limit(10);
      
      if (error) throw error;
      return mapAndHydrateLegRows(data);
    },
    enabled: !!driverId && !!tenantId,
    refetchInterval: 5 * 60 * 1000,
  });

  return {
    currentAssignment,
    nextAssignment: currentAssignment ? null : nextAssignment,
    currentAssignments: currentAssignments || [],
    upcomingAssignments: upcomingAssignments || [],
    recentlyCompleted: recentlyCompleted || [],
    isLoading: loadingCurrent || loadingNext || loadingUpcoming || loadingCompleted || loadingCurrentList,
    activeAssignment: currentAssignment || nextAssignment,
  };
}
